<template>
  <div class="aspect-video w-full">
    <div v-if="isVisible" v-show="isPlayerReady" id="player" ref="containerRef" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Player from "@vimeo/player";
import { reportVideoPlayEvent } from "@/composables/user-activity-monitor";

const props = defineProps<{ vimeoID: any; }>();
const emit = defineEmits<{ ready: [ player: Player ] }>();

const containerRef = ref<HTMLDivElement>();
let player: Player | null = null;

const isVisible = computed( () => !!props.vimeoID );
const getVideoSrc = computed( () => {
  if ( typeof props.vimeoID === 'string' ) {
    if ( props.vimeoID.includes( 'http' ) || props.vimeoID.includes( '.com' ) ) {
      const parts = props.vimeoID.split( '/' );
      return parts[ parts.length - 1 ];
    }
  }

  return props.vimeoID;
} );

const isPlayerReady = ref<boolean>( false );
const hasPlayed = ref<boolean>( false );
const vimeoInfo = ref( {
  id: getVideoSrc.value,
  title: '',
  duration: 0
} );

function initPlayer() {
  if ( !containerRef.value || !getVideoSrc.value ) return;
  destroyPlayer();

  player = new Player( containerRef.value, {
    id: getVideoSrc.value,
    controls: true,
    responsive: true
  } );

  player.ready().then( () => {
    // Unmute video on mobile since it's user-initiated
    player!.setMuted( false ).catch( () => {
      // Fallback if unmuting fails due to autoplay policies
    } );

    isPlayerReady.value = true;
    emit( 'ready', player! );
  } );

  player.on( 'play', onPlay );
}

function destroyPlayer() {
  if ( player ) {
    player.destroy().catch( () => {} );
    player = null;
    isPlayerReady.value = false;
  }
}

function onPlay() {
  if ( !player ) return;

  Promise.all( [
    player.getVideoId(),
    player.getVideoTitle(),
    player.getDuration()
  ] ).then( ( [ id, title, duration ] ) => {
    vimeoInfo.value = { id, title, duration };
  } );

  if ( !hasPlayed.value ) {
    reportVideoPlayEvent( vimeoInfo.value.id );
  }
  hasPlayed.value = true;
}

watch( () => props.vimeoID, () => {
  hasPlayed.value = false;
  nextTick( () => initPlayer() );
} );

onMounted( () => {
  if ( isVisible.value ) {
    initPlayer();
  }
} );

onBeforeUnmount( () => {
  destroyPlayer();
} );

function pauseVideo() {
  player?.pause();
}

async function playVideo() {
  if ( player ) {
    await player.setMuted( false );
    await player.play();
  }
}

defineExpose( { pauseVideo, playVideo, player } );
</script>

<style>
#player iframe {
  aspect-ratio: 16 / 9 !important;
  width: 100% !important;
  height: auto !important;
  border-radius: 0.5rem !important;
}
</style>
